import React, { useEffect, useState } from "react";
import {useField, useFormikContext} from "formik";
import { filterBy } from "@progress/kendo-data-query";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { FieldError } from "../Error/FieldError.jsx";
import { ObjUtils } from "../../../utils/ObjUtils.js";

/**
 * Please read: use this component when you have an array of objects: [{id: 1, display: "option 1"}, ...]
 * If you have a primitive array of data ["option 1", "option 2"], use the PrimitiveDropdownListInput component.
 **/
export const DropdownListInput = ({ label, required, style, disabled = false, onChange, ...otherProps }) => {
    const [field, meta, helpers] = useField(otherProps);
    const formikContext = useFormikContext();
    const { setFieldValue, values } = formikContext;
    const [data, setData] = useState([]);
    const [filter, setFilter] = useState(null);
    const {
        tabIndex,
        filterable = false,
        data: propData = [],
        textField = "text",
    } = otherProps;



    useEffect(() => {
        if (propData && Array.isArray(propData) && propData.length > 0) {
            if (!ObjUtils.isObject(propData[0])) {
                console.error(`Error: The DropdownList data options must follow this format: [{id: 1, display: "option1"}, ...] 
                If you are using an array of primitive values, then use the PrimitiveDropdownListInput component...`);
            }
            setData(propData);
        }
    }, [propData]);

    const onBlur = () => {
        setData(propData);
        setFilter(null);
        field.onBlur(field.name);
    };

    const onClose = () => {
        setData(propData);
        setFilter(null);
    };

    const filterDropdownListData = (filter) => {
        const filteredData = propData.slice();
        return filterBy(filteredData, filter);
    };

    const onFilterChange = (event) => {
        setFilter(event.filter.value);
        setData(filterDropdownListData(event.filter));
    };

    // If disabled is a function, call it with the full form context
    const isDisabled = typeof disabled === 'function'
        ? disabled({
            ...formikContext,
            fieldName: otherProps.name,
            currentValue: values[otherProps.name]
        })
        : disabled;

    const handleChange = async (e) => {
        const newValue = e.target.value;

        if (onChange) {
            // Let the custom onChange handle field updates
            await onChange(e, {
                ...formikContext,
                fieldName: otherProps.name,
                newValue,
                previousValue: values[otherProps.name]
            });
        } else {
            // Default behavior: update Formik's state
            await setFieldValue(otherProps.name, newValue);
        }
    };

    return (
        <>
            {label && (
                <label htmlFor={otherProps.id || otherProps.name}>
                    {label}
                    {required && <span style={{ color: "red", marginLeft: 4 }}>*</span>}
                </label>
            )}
            <DropDownList
                id={otherProps.id || otherProps.name}
                name={field.name}
                data={data}
                value={field.value}
                onChange={handleChange}
                onBlur={onBlur}
                disabled={isDisabled}
                tabIndex={tabIndex}
                filterable={filterable}
                onFilterChange={onFilterChange}
                onClose={onClose}
                filter={filter}
                textField={textField}
                {...otherProps}
            />
            <FieldError name={otherProps.name} />
        </>
    );
};